/**
 * Centralized language configuration
 * Single source of truth for all language settings
 */

// Language configuration interface
export interface LanguageConfig {
  code: string;
  name: {
    native: string;
    english: string;
  };
  isRtl?: boolean; // Optional, defaults to false
}

// All supported languages configuration
export const LANGUAGES: Record<string, LanguageConfig> = {
  en: {
    code: "en",
    name: { native: "English", english: "English" },
  },
  ru: {
    code: "ru",
    name: { native: "Русский", english: "Russian" },
  },
  es: {
    code: "es",
    name: { native: "Español", english: "Spanish" },
  },
  de: {
    code: "de",
    name: { native: "Deutsch", english: "German" },
  },
  tr: {
    code: "tr",
    name: { native: "Türkçe", english: "Turkish" },
  },
  fr: {
    code: "fr",
    name: { native: "Français", english: "French" },
    // tonConnectLocale defaults to "en", isRtl defaults to false
  },
  in: {
    code: "in",
    name: { native: "हिन्दी", english: "Hindi" },
    // tonConnectLocale defaults to "en", isRtl defaults to false
  },
  zh: {
    code: "zh",
    name: { native: "中文", english: "Chinese" },
    // tonConnectLocale defaults to "en", isRtl defaults to false
  },
  pt: {
    code: "pt",
    name: { native: "Português", english: "Portuguese" },
    // tonConnectLocale defaults to "en", isRtl defaults to false
  },
  ja: {
    code: "ja",
    name: { native: "日本語", english: "Japanese" },
    // tonConnectLocale defaults to "en", isRtl defaults to false
  },
  ko: {
    code: "ko",
    name: { native: "한국어", english: "Korean" },
    // tonConnectLocale defaults to "en", isRtl defaults to false
  },
  it: {
    code: "it",
    name: { native: "Italiano", english: "Italian" },
    // tonConnectLocale defaults to "en", isRtl defaults to false
  },
  uk: {
    code: "uk",
    name: { native: "Українська", english: "Ukrainian" },
    // tonConnectLocale defaults to "en", isRtl defaults to false
  },
  ca: {
    code: "ca",
    name: { native: "Català", english: "Catalan" },
    // tonConnectLocale defaults to "en", isRtl defaults to false
  },
  hr: {
    code: "hr",
    name: { native: "Hrvatski", english: "Croatian" },
    // tonConnectLocale defaults to "en", isRtl defaults to false
  },
  cs: {
    code: "cs",
    name: { native: "Čeština", english: "Czech" },
    // tonConnectLocale defaults to "en", isRtl defaults to false
  },
  nl: {
    code: "nl",
    name: { native: "Nederlands", english: "Dutch" },
    // tonConnectLocale defaults to "en", isRtl defaults to false
  },
  fi: {
    code: "fi",
    name: { native: "Suomi", english: "Finnish" },
    // tonConnectLocale defaults to "en", isRtl defaults to false
  },
  hu: {
    code: "hu",
    name: { native: "Magyar", english: "Hungarian" },
    // tonConnectLocale defaults to "en", isRtl defaults to false
  },
  id: {
    code: "id",
    name: { native: "Bahasa Indonesia", english: "Indonesian" },
    // tonConnectLocale defaults to "en", isRtl defaults to false
  },
  kk: {
    code: "kk",
    name: { native: "Қазақ", english: "Kazakh" },
    // tonConnectLocale defaults to "en", isRtl defaults to false
  },
  ms: {
    code: "ms",
    name: { native: "Bahasa Melayu", english: "Malay" },
    // tonConnectLocale defaults to "en", isRtl defaults to false
  },
  no: {
    code: "no",
    name: { native: "Norsk", english: "Norwegian" },
    // tonConnectLocale defaults to "en", isRtl defaults to false
  },
  pl: {
    code: "pl",
    name: { native: "Polski", english: "Polish" },
    // tonConnectLocale defaults to "en", isRtl defaults to false
  },
  sr: {
    code: "sr",
    name: { native: "Српски", english: "Serbian" },
    // tonConnectLocale defaults to "en", isRtl defaults to false
  },
  sk: {
    code: "sk",
    name: { native: "Slovenčina", english: "Slovak" },
    // tonConnectLocale defaults to "en", isRtl defaults to false
  },
  sv: {
    code: "sv",
    name: { native: "Svenska", english: "Swedish" },
    // tonConnectLocale defaults to "en", isRtl defaults to false
  },
  uz: {
    code: "uz",
    name: { native: "Oʻzbek", english: "Uzbek" },
    // tonConnectLocale defaults to "en", isRtl defaults to false
  },
  ha: {
    code: "ha",
    name: { native: "Hausa", english: "Hausa" },
    // tonConnectLocale defaults to "en", isRtl defaults to false
  },
  yo: {
    code: "yo",
    name: { native: "Yorùbá", english: "Yoruba" },
    // tonConnectLocale defaults to "en", isRtl defaults to false
  },
  jv: {
    code: "jv",
    name: { native: "ꦧꦱꦗꦮ", english: "Javanese" },
    // tonConnectLocale defaults to "en", isRtl defaults to false
  },
  mr: {
    code: "mr",
    name: { native: "मराठी", english: "Marathi" },
    // tonConnectLocale defaults to "en", isRtl defaults to false
  },
  te: {
    code: "te",
    name: { native: "తెలుగు", english: "Telugu" },
    // tonConnectLocale defaults to "en", isRtl defaults to false
  },
  vi: {
    code: "vi",
    name: { native: "Tiếng Việt", english: "Vietnamese" },
    // tonConnectLocale defaults to "en", isRtl defaults to false
  },
  ar: {
    code: "ar",
    name: { native: "العربية", english: "Arabic" },
    isRtl: true,
  },
  fa: {
    code: "fa",
    name: { native: "فارسی", english: "Persian" },
    isRtl: true,
  },
  he: {
    code: "he",
    name: { native: "עברית", english: "Hebrew" },
    isRtl: true,
  },
} as const;

// Derived configurations
export const supportedLanguages = Object.keys(LANGUAGES) as Array<
  keyof typeof LANGUAGES
>;
export const fallbackLanguage = "en" as const;

// Helper functions
export function getLanguageConfig(code: string): LanguageConfig | undefined {
  return LANGUAGES[code as keyof typeof LANGUAGES];
}

export function isSupportedLanguage(
  code: string
): code is keyof typeof LANGUAGES {
  return code in LANGUAGES;
}

export function getLanguageDisplayName(code: string): {
  native: string;
  english: string;
} {
  const config = getLanguageConfig(code);
  return config?.name || { native: code, english: code };
}

export function isRightToLeft(code: string): boolean {
  const config = getLanguageConfig(code);
  return config?.isRtl || false;
}

export function getAvailableLanguages(): LanguageConfig[] {
  return Object.values(LANGUAGES).sort((a, b) =>
    a.name.english.localeCompare(b.name.english)
  );
}

// Type exports
export type SupportedLanguage = keyof typeof LANGUAGES;
